import React from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutAnimation } from 'react-native';

import { Button } from '@/components/ButtonComponent';
import { Box } from '@/components/lib/components';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useActiveWallet } from '@/hooks/use-active-wallet';
import { useRouteByName } from '@/hooks/use-route-by-name';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { DepositStackParamList } from '@/navigation/types';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { useDepositButtonNavigation } from '../hooks/use-deposit-button-navigation';
import { PaymentMethod, PreferredPayment } from '../hooks/use-deposit-payment-methods';
import { setDepositAmount, setRetryAmount } from '../hooks/use-deposit-status-store';

type DepositButtonProps = {
    isEditing: boolean;
    errorMessage: string;
    selectedAmount: string;
    hasMadeFirstDeposit?: boolean;
    selectedPaymentMethod?: PaymentMethod;
    selectedPreferredMethod?: PreferredPayment;
    setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
};

export const DepositButton = ({
    isEditing,
    setIsEditing,
    errorMessage,
    selectedAmount,
    hasMadeFirstDeposit,
    selectedPaymentMethod,
    selectedPreferredMethod,
}: DepositButtonProps) => {
    const { t } = useTranslation('wallet');
    const tSocialWallet = useSocialWalletTranslation('wallet');

    const depositRoute = useRouteByName<DepositStackParamList, 'DepositScreen'>('DepositScreen');

    // This is a feature config that determines if only debit card deposits are allowed after KYC success
    const forceDepositWithDebitCard = depositRoute?.params?.forceDepositWithDebitCard;

    const handleNavigation = useDepositButtonNavigation({
        selectedPaymentMethod,
        selectedPreferredMethod,
        forceDepositWithDebitCard,
    });

    const setIsEditingFalse = () => {
        LayoutAnimation.easeInEaseOut();
        setIsEditing(false);
    };

    const handleDepositPress = async () => {
        setDepositAmount(selectedAmount);
        setRetryAmount(selectedAmount); // Used to store the initial amount on funds guidance
        const isSubmittingDeposit = forceDepositWithDebitCard || !isEditing;
        const isFirstTimeDeposit = !hasMadeFirstDeposit && isSubmittingDeposit;
        const params = {
            amount: selectedAmount,
            method: selectedPaymentMethod?.name,
            kycCTADebitOnly: forceDepositWithDebitCard,
        };

        if (isFirstTimeDeposit) {
            BetrAnalytics.trackProductEvent(AnalyticsEvent.FTD_SUBMIT, params);
        }

        BetrAnalytics.trackProductEvent(
            isSubmittingDeposit ? AnalyticsEvent.DEPOSIT_SUBMIT : AnalyticsEvent.DEPOSIT_SUBMIT_EDIT,
            isSubmittingDeposit ? params : undefined
        );

        if (isSubmittingDeposit) {
            handleNavigation();

            // Wait for the navigation transition to complete before updating the layout
            setTimeout(() => {
                setIsEditingFalse();
            }, 0);
        } else {
            // If no navigation, proceed with the layout animation immediately
            setIsEditingFalse();
        }
    };

    const isErrorOrZeroAmount = !!errorMessage || Number(selectedAmount) === 0;
    const isDisabled = isEditing ? isErrorOrZeroAmount : !selectedPaymentMethod;

    const { activeWalletProductName, isSharedSocialWallet } = useActiveWallet();
    const getButtonLabel = (): string => {
        if (forceDepositWithDebitCard) {
            return tSocialWallet('deposit_with_debit');
        }
        if (isEditing) {
            return tSocialWallet('deposit_button_label_editing');
        }
        if (selectedPreferredMethod?.is_expired) {
            return t('deposit_button_label_expired');
        }
        return isSharedSocialWallet
            ? t('purchase_button_label', { selectedAmount: toLocaleCurrency(selectedAmount) })
            : t('deposit_button_label', {
                  selectedAmount: toLocaleCurrency(selectedAmount),
                  activeWalletProductName,
              });
    };

    if (!isEditing && !selectedPaymentMethod) {
        return null;
    }

    return (
        <Box p="s16" justifyContent={'center'}>
            <Button hierarchy={'primary'} onPress={handleDepositPress} disabled={isDisabled} label={getButtonLabel()} />
        </Box>
    );
};
